import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import {
  Box, Typography, Menu, MenuItem, ListItemIcon,
  ListItemText, Divider, CircularProgress, Tooltip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  AddOutlined,
  DriveFileRenameOutlineOutlined, ContentCopyOutlined, DeleteOutlineOutlined,
  MeetingRoomOutlined, StorefrontOutlined, RecordVoiceOverOutlined,
  StarOutlined,
} from '@mui/icons-material';
import { PRIMARY } from './constants';

// ── LobbyThumbnail ────────────────────────────────────────────────────────────
// Relies solely on generated screenshots. Shows a typed placeholder until one
// is available.
function LobbyThumbnail({ lobby, screenshot }) {
  if (screenshot) {
    return (
      <Box sx={{
        position: 'absolute', inset: 0, borderRadius: '6px', overflow: 'hidden',
        backgroundImage: `url(${screenshot})`,
        backgroundSize: 'cover', backgroundPosition: 'center',
      }} />
    );
  }

  // Placeholder gradient keyed to lobby type while screenshot is pending
  const gradients = {
    booth:      'linear-gradient(135deg, #2e1065 0%, #4c1d95 100%)',
    carousel:   'linear-gradient(135deg, #0f2417 0%, #0d7a5f 100%)',
    plenary:    'linear-gradient(135deg, #1e1b4b 0%, #4338ca 100%)',
    exhibition: 'linear-gradient(135deg, #0f2417 0%, #0d7a5f 100%)',
    lobby:      'linear-gradient(135deg, #1a2540 0%, #2d4a6b 100%)',
  };
  const icons = {
    booth:      '🏪',
    carousel:   '🎞',
    plenary:    '🎙',
    exhibition: '🏛',
    lobby:      '🚪',
  };
  const bg   = gradients[lobby.lobbyType] || gradients.lobby;
  const icon = icons[lobby.lobbyType]    || icons.lobby;

  return (
    <Box sx={{
      position: 'absolute', inset: 0, borderRadius: '6px', overflow: 'hidden',
      background: bg,
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', gap: 0.5,
    }}>
      <Box sx={{ fontSize: 20, lineHeight: 1, opacity: 0.6 }}>{icon}</Box>
      <Box sx={{ fontSize: '0.5rem', fontWeight: 600, color: 'rgba(255,255,255,0.35)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
        No preview
      </Box>
    </Box>
  );
}

// ── LobbyTabs ─────────────────────────────────────────────────────────────────
export function LobbyTabs({
  lobbies = [],
  activeLobbyId,
  onSwitch,
  onAdd,
  onRename,
  onDuplicate,
  onDelete,
  onSetDefault,
  onReorder,
  loading,
  screenshots = {},
}) {
  const [renamingId,  setRenamingId]  = useState(null);
  const [renameVal,   setRenameVal]   = useState('');
  const [menuPos,     setMenuPos]     = useState(null);
  const [menuLobbyId, setMenuLobbyId] = useState(null);
  const [addPopoverOpen, setAddPopoverOpen] = useState(false);
  const addBtnRef = useRef(null);
  const inputRef  = useRef(null);

  // Close add popover on outside click
  useEffect(() => {
    if (!addPopoverOpen) return;
    const handler = (e) => {
      if (addBtnRef.current && !addBtnRef.current.contains(e.target)) setAddPopoverOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [addPopoverOpen]);

  // Drag state
  const dragSrcRef  = useRef(null);
  const [dragOverId,  setDragOverId]  = useState(null);
  const [localOrder,  setLocalOrder]  = useState(null);

  useEffect(() => { setLocalOrder(null); }, [lobbies]);

  const displayLobbies = localOrder ?? lobbies;

  useEffect(() => {
    if (renamingId && inputRef.current) inputRef.current.select();
  }, [renamingId]);

  const startRename = (lobby) => { setMenuPos(null); setRenamingId(lobby._id); setRenameVal(lobby.name); };
  const commitRename = () => {
    if (renamingId && renameVal.trim()) onRename(renamingId, renameVal.trim());
    setRenamingId(null);
  };
  const openMenu  = (e, lobbyId) => { e.preventDefault(); e.stopPropagation(); setMenuPos({ x: e.clientX, y: e.clientY }); setMenuLobbyId(lobbyId); };
  const closeMenu = () => { setMenuPos(null); setMenuLobbyId(null); };

  const handleDragStart = (e, id) => { dragSrcRef.current = id; e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', id); };
  const handleDragEnter = (id) => {
    if (!dragSrcRef.current || dragSrcRef.current === id) return;
    const srcIdx = lobbies.findIndex(l => l._id === dragSrcRef.current);
    const tgtIdx = lobbies.findIndex(l => l._id === id);
    if (srcIdx === -1 || tgtIdx === -1) return;
    const reordered = [...lobbies];
    const [moved] = reordered.splice(srcIdx, 1);
    reordered.splice(tgtIdx, 0, moved);
    setLocalOrder(reordered);
    setDragOverId(id);
  };
  const handleDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };
  const handleDrop = (e) => {
    e.preventDefault();
    if (!dragSrcRef.current || !localOrder) { setDragOverId(null); return; }
    onReorder?.(localOrder.map((l, i) => ({ ...l, order: i })));
    setDragOverId(null);
    dragSrcRef.current = null;
  };
  const handleDragEnd = () => { setDragOverId(null); dragSrcRef.current = null; };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 3, py: 2, borderTop: '1px solid #e8edf2', background: '#fafbfc', flexShrink: 0 }}>
        <CircularProgress size={16} />
        <Typography sx={{ fontSize: '0.78rem', color: '#9ea8b3' }}>Loading…</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: '6px',
      px: 2, py: '10px',
      borderTop: '1px solid #e8edf2',
      background: '#fafbfc',
      overflowX: 'auto', flexShrink: 0,
      '&::-webkit-scrollbar': { height: 4 },
      '&::-webkit-scrollbar-thumb': { background: '#d4dbe6', borderRadius: 2 },
    }}>

      {lobbies.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, pr: 0.5, flexShrink: 0 }}>
          <MeetingRoomOutlined sx={{ fontSize: 10, color: '#c4cdd8' }} />
          <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, color: '#c4cdd8', letterSpacing: '0.07em', textTransform: 'uppercase' }}>
            Lobbies
          </Typography>
        </Box>
      )}

      {displayLobbies.map((lobby, idx) => {
        const isActive   = lobby._id === activeLobbyId;
        const isDragSrc  = dragSrcRef.current === lobby._id;
        const isDragOver = dragOverId === lobby._id;
        const screenshot = screenshots[lobby._id] || null;

        return (
          <Box
            key={lobby._id}
            draggable
            onDragStart={e => handleDragStart(e, lobby._id)}
            onDragEnter={() => handleDragEnter(lobby._id)}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onDragEnd={handleDragEnd}
            onDoubleClick={() => startRename(lobby)}
            onClick={() => { if (!renamingId) onSwitch(lobby._id); }}
            onContextMenu={e => openMenu(e, lobby._id)}
            sx={{
              position: 'relative', flexShrink: 0,
              width: 150, height: 100, borderRadius: '8px',
              cursor: isDragSrc ? 'grabbing' : 'grab',
              border: isActive
                ? `2px solid ${PRIMARY}`
                : isDragOver
                  ? `2px dashed ${alpha(PRIMARY, 0.6)}`
                  : '1px solid #d4dbe6',
              background: isActive ? '#fff' : '#f0f4f8',
              display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'flex-end',
              p: '5px 7px',
              transition: 'border-color 0.12s, box-shadow 0.12s, opacity 0.15s, transform 0.15s',
              boxShadow: isDragOver
                ? `0 0 0 3px ${alpha(PRIMARY, 0.18)}`
                : isActive
                  ? `0 0 0 3px ${alpha(PRIMARY, 0.12)}`
                  : 'none',
              overflow: 'hidden',
              opacity: isDragSrc ? 0.4 : 1,
              transform: isDragOver ? 'scale(1.03)' : 'scale(1)',
              '&:hover': { borderColor: isActive ? PRIMARY : '#b0bac8', background: isActive ? '#fff' : '#eaf0f7' },
            }}
          >
            {/* Thumbnail */}
            <LobbyThumbnail lobby={lobby} screenshot={screenshot} />

            {/* BOOTH badge */}
            {lobby.lobbyType === 'booth' && (
              <Box sx={{ position: 'absolute', top: 3, left: 3, background: '#7c3aed', color: '#fff', fontSize: '0.5rem', fontWeight: 700, px: 0.5, py: 0.2, borderRadius: '3px', zIndex: 2, letterSpacing: '0.04em' }}>
                BOOTH
              </Box>
            )}

            {/* SLIDES badge */}
            {lobby.lobbyType === 'carousel' && (
              <Box sx={{ position: 'absolute', top: 3, left: 3, background: '#c2410c', color: '#fff', fontSize: '0.5rem', fontWeight: 700, px: 0.5, py: 0.2, borderRadius: '3px', zIndex: 2, letterSpacing: '0.04em' }}>
                SLIDES
              </Box>
            )}

            {lobby.isDefault && (
              <Box sx={{
                position: 'absolute', top: 3,
                left: (lobby.lobbyType === 'booth' || lobby.lobbyType === 'carousel') ? 45 : 3,
                background: PRIMARY, color: '#fff', fontSize: '0.55rem', fontWeight: 700,
                px: 0.5, py: 0.2, borderRadius: '3px', zIndex: 2, letterSpacing: '0.04em',
              }}>
                DEFAULT
              </Box>
            )}

            {/* Screenshot freshness indicator — subtle dot when a screenshot exists */}
            {screenshot && (
              <Box sx={{
                position: 'absolute', top: 3, right: 3,
                width: 5, height: 5, borderRadius: '50%',
                background: 'rgba(34,197,94,0.85)',
                border: '1px solid rgba(255,255,255,0.6)',
                zIndex: 2,
              }} />
            )}

            {/* Name / rename input */}
            {renamingId === lobby._id ? (
              <Box
                component="input"
                ref={inputRef}
                value={renameVal}
                onChange={e => setRenameVal(e.target.value)}
                onBlur={commitRename}
                onKeyDown={e => { if (e.key === 'Enter') commitRename(); if (e.key === 'Escape') setRenamingId(null); }}
                onClick={e => e.stopPropagation()}
                sx={{ position: 'relative', zIndex: 1, width: '100%', border: 'none', outline: 'none', background: 'transparent', fontSize: '0.68rem', fontWeight: 700, color: PRIMARY, fontFamily: 'inherit', p: 0 }}
              />
            ) : (
              <Typography sx={{
                position: 'relative', zIndex: 1,
                fontSize: '0.68rem', fontWeight: isActive ? 700 : 500,
                color: isActive ? PRIMARY : '#7a8899',
                lineHeight: 1, userSelect: 'none',
                textShadow: screenshot ? '0 1px 3px rgba(0,0,0,0.7), 0 0 6px rgba(0,0,0,0.4)' : 'none',
              }}>
                {lobby.name || `Lobby ${idx + 1}`}
              </Typography>
            )}
          </Box>
        );
      })}

      {/* Add button */}
      <Box sx={{ flexShrink: 0 }} ref={addBtnRef}>
        <Tooltip title="Add lobby" placement="top" arrow>
          <Box
            onClick={() => setAddPopoverOpen(v => !v)}
            sx={{
              height: 100, minWidth: 52, borderRadius: '8px',
              border: addPopoverOpen ? `1px solid ${PRIMARY}` : '1px dashed #c4cdd8',
              background: addPopoverOpen ? alpha(PRIMARY, 0.06) : 'transparent',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', color: addPopoverOpen ? PRIMARY : '#9ea8b3', transition: 'all 0.12s', px: 1.5,
              '&:hover': { borderColor: PRIMARY, color: PRIMARY, background: alpha(PRIMARY, 0.04) },
            }}
          >
            <AddOutlined sx={{ fontSize: 18 }} />
          </Box>
        </Tooltip>
      </Box>

      {/* Type picker portal */}
      {addPopoverOpen && (() => {
        const rect = addBtnRef.current?.getBoundingClientRect();
        if (!rect) return null;
        return ReactDOM.createPortal(
          <Box
            onMouseDown={e => e.stopPropagation()}
            sx={{
              position: 'fixed',
              bottom: `${window.innerHeight - rect.top + 8}px`,
              left: `${rect.left + rect.width / 2 - 114}px`,
              zIndex: 1500,
              background: '#fff',
              borderRadius: '12px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.08)',
              border: '1px solid #e8edf2',
              p: 1.5,
              width: 228,
            }}
          >
            <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#9ea8b3', textTransform: 'uppercase', letterSpacing: '0.08em', px: 0.5, mb: 1 }}>
              Choose type
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1 }}>
              {[
                { type: 'lobby',      label: 'Lobby',      Icon: MeetingRoomOutlined,     color: '#1075BA' },
                { type: 'exhibition', label: 'Exhibition',  Icon: StorefrontOutlined,      color: '#0d7a5f' },
                { type: 'plenary',    label: 'Plenary',     Icon: RecordVoiceOverOutlined, color: '#7c3aed' },
              ].map(({ type, label, Icon, color }) => (
                <Box
                  key={type}
                  onClick={() => { setAddPopoverOpen(false); onAdd(type); }}
                  sx={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    gap: 0.75, py: 1.5, px: 0.5, borderRadius: '8px', cursor: 'pointer',
                    border: '1.5px solid #e8edf2', transition: 'all 0.13s',
                    '&:hover': { borderColor: color, background: alpha(color, 0.06) },
                  }}
                >
                  <Box sx={{ width: 32, height: 32, borderRadius: '8px', background: alpha(color, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center', color }}>
                    <Icon sx={{ fontSize: 17 }} />
                  </Box>
                  <Typography sx={{ fontSize: '0.63rem', fontWeight: 600, color: '#3c4555', lineHeight: 1, textAlign: 'center' }}>
                    {label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>,
          document.body
        );
      })()}

      {/* Right-click context menu */}
      <Menu
        open={Boolean(menuPos) && Boolean(menuLobbyId)}
        onClose={closeMenu}
        anchorReference="anchorPosition"
        anchorPosition={menuPos ? { top: menuPos.y, left: menuPos.x } : undefined}
        PaperProps={{ sx: { boxShadow: '0 4px 20px rgba(0,0,0,0.12)', borderRadius: 2, minWidth: 170 } }}
      >
        {(() => {
          const menuLobby = lobbies.find(l => l._id === menuLobbyId);
          return !menuLobby?.isDefault && (
            <MenuItem onClick={() => { onSetDefault(menuLobbyId); closeMenu(); }} sx={{ fontSize: '0.82rem', color: '#b45309' }}>
              <ListItemIcon><StarOutlined sx={{ fontSize: 16, color: '#b45309' }} /></ListItemIcon>
              <ListItemText>Set as default</ListItemText>
            </MenuItem>
          );
        })()}
        <MenuItem onClick={() => { const l = lobbies.find(l => l._id === menuLobbyId); if (l) startRename(l); }} sx={{ fontSize: '0.82rem' }}>
          <ListItemIcon><DriveFileRenameOutlineOutlined sx={{ fontSize: 16 }} /></ListItemIcon>
          <ListItemText>Rename</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { onDuplicate(menuLobbyId); closeMenu(); }} sx={{ fontSize: '0.82rem' }}>
          <ListItemIcon><ContentCopyOutlined sx={{ fontSize: 16 }} /></ListItemIcon>
          <ListItemText>Duplicate</ListItemText>
        </MenuItem>
        {lobbies.length > 1 && [
          <Divider key="div" />,
          <MenuItem key="del" onClick={() => { onDelete(menuLobbyId); closeMenu(); }} sx={{ fontSize: '0.82rem', color: '#dc2626' }}>
            <ListItemIcon><DeleteOutlineOutlined sx={{ fontSize: 16, color: '#dc2626' }} /></ListItemIcon>
            <ListItemText>Delete lobby</ListItemText>
          </MenuItem>,
        ]}
      </Menu>
    </Box>
  );
}